import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import { useLanguage } from '../../context/LanguageContext';
import { TOTAL_WIZARD_STEPS } from '../../constants/wizardSteps';
import { WizardNavigation, StepIndicator } from './WizardButton';
import {
  EDUCATION_LEVELS,
  STUDY_FIELDS,
  COURSE_OF_STUDY_OPTIONS
} from '../../constants/educationOptions';

/**
 * Education component for collecting user's educational background
 * @param {Object} props - Component props
 * @param {number} props.stepNumber - Current step number in wizard flow
 * @param {string} props.title - Step title displayed to user
 * @param {string} props.description - Optional description text
 * @param {Function} props.onNext - Callback when user proceeds to next step
 * @param {Function} props.onPrevious - Callback when user goes to previous step
 * @param {boolean} props.canGoNext - Whether next button should be enabled
 * @param {boolean} props.canGoPrevious - Whether previous button should be enabled
 * @param {string} props.className - Additional CSS classes
 */
const Education = ({
  stepNumber = 4,
  totalSteps = TOTAL_WIZARD_STEPS,
  title = "",
  description = "",
  onNext,
  onPrevious,
  canGoNext = false,
  canGoPrevious = true,
  className = ''
}) => {
  const { state, actions } = useWizard();
  const { t } = useLanguage();

  // Auto-scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  // Use translations with fallbacks
  const stepTitle = title || t('wizard.education.title', 'Educational Background');
  const stepDescription = description || t('wizard.education.description', 'Tell us about your education so we can assess your eligibility for various immigration programs.');
  const [formData, setFormData] = useState({
    highestEducation: state.education.highestEducation || '',
    fieldOfStudy: state.education.fieldOfStudy || '',
    courseOfStudy: state.education.courseOfStudy || '',
    university: state.education.university || '',
    graduationYear: state.education.graduationYear || '',
    grades: state.education.grades || '',
    additionalCertificates: state.education.additionalCertificates || ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.highestEducation) {
      newErrors.highestEducation = t('wizard.education.errors.highestEducation.required', 'Please select your highest education level');
    }

    if (!formData.fieldOfStudy) {
      newErrors.fieldOfStudy = t('wizard.education.errors.fieldOfStudy.required', 'Please select your field of study');
    }

    if (!formData.courseOfStudy) {
      newErrors.courseOfStudy = t('wizard.education.errors.courseOfStudy.required', 'Please select your course of study');
    }

    if (!formData.university.trim()) {
      newErrors.university = t('wizard.education.errors.university.required', 'University/Institution name is required');
    }

    if (!formData.graduationYear) {
      newErrors.graduationYear = t('wizard.education.errors.graduationYear.required', 'Graduation year is required');
    } else {
      const year = parseInt(formData.graduationYear);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1950 || year > currentYear + 10) {
        newErrors.graduationYear = t('wizard.education.errors.graduationYear.invalid', 'Please enter a valid graduation year');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, t]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  }, [errors]);

  const handleNext = useCallback(() => {
    if (validateForm()) {
      // Update wizard state with education info
      actions.updateEducation(formData);

      if (onNext) {
        onNext({
          education: formData
        });
      }
    }
  }, [formData, validateForm, actions, onNext]);

  const canProceed = useMemo(() => {
    const requiredFields = ['highestEducation', 'fieldOfStudy', 'courseOfStudy', 'university', 'graduationYear'];
    return requiredFields.every(field => formData[field] && formData[field].trim()) &&
      Object.keys(errors).every(field => !errors[field]);
  }, [formData, errors]);

  // Validate on mount to show any initial errors
  React.useEffect(() => {
    if (Object.keys(formData).some(key => formData[key])) {
      validateForm();
    }
  }, []);

  return (
    <div className={`education-step w-full mx-auto ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="mb-2">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {t('wizard.common.stepIndicator', 'Step {{current}} of {{total}}', {
              current: stepNumber,
              total: totalSteps
            })}
          </span>
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">{stepTitle}</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">{stepDescription}</p>
      </div>

      {/* Form */}
      <div className="card p-6 sm:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Highest Education */}
          <div>
            <label htmlFor="highestEducation" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.education.fields.highestEducation.label', 'Highest Education Level *')}
            </label>
            <select
              id="highestEducation"
              value={formData.highestEducation}
              onChange={(e) => handleInputChange('highestEducation', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.highestEducation ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">{t('wizard.education.fields.highestEducation.placeholder', 'Select your highest education')}</option>
              {EDUCATION_LEVELS.map(level => (
                <option key={level.value} value={level.value}>{t(level.key, level.value)}</option>
              ))}
            </select>
            {errors.highestEducation && (
              <p className="mt-1 text-sm text-red-600">{errors.highestEducation}</p>
            )}
          </div>

          {/* Field of Study */}
          <div>
            <label htmlFor="fieldOfStudy" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.education.fields.fieldOfStudy.label', 'Field of Study *')}
            </label>
            <select
              id="fieldOfStudy"
              value={formData.fieldOfStudy}
              onChange={(e) => handleInputChange('fieldOfStudy', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.fieldOfStudy ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">{t('wizard.education.fields.fieldOfStudy.placeholder', 'Select your field of study')}</option>
              {STUDY_FIELDS.map(field => (
                <option key={field.value} value={field.value}>{t(field.key, field.value)}</option>
              ))}
            </select>
            {errors.fieldOfStudy && (
              <p className="mt-1 text-sm text-red-600">{errors.fieldOfStudy}</p>
            )}
          </div>

          {/* Course of Study */}
          <div>
            <label htmlFor="courseOfStudy" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.education.fields.courseOfStudy.label', 'Course of Study *')}
            </label>
            <select
              id="courseOfStudy"
              value={formData.courseOfStudy}
              onChange={(e) => handleInputChange('courseOfStudy', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.courseOfStudy ? 'border-red-500' : 'border-gray-300'
                }`}
            >
              <option value="">{t('wizard.education.fields.courseOfStudy.placeholder', 'Select your course of study')}</option>
              {COURSE_OF_STUDY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{t(option.key, option.value)}</option>
              ))}
            </select>
            {errors.courseOfStudy && (
              <p className="mt-1 text-sm text-red-600">{errors.courseOfStudy}</p>
            )}
          </div>

          {/* University */}
          <div>
            <label htmlFor="university" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.education.fields.university.label', 'University/Institution/School *')}
            </label>
            <input
              type="text"
              id="university"
              value={formData.university}
              onChange={(e) => handleInputChange('university', e.target.value)}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.university ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('wizard.education.fields.university.placeholder', 'Enter your university name')}
            />
            {errors.university && (
              <p className="mt-1 text-sm text-red-600">{errors.university}</p>
            )}
          </div>

          {/* Graduation Year */}
          <div>
            <label htmlFor="graduationYear" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.education.fields.graduationYear.label', 'Graduation Year / A Level exam year *')}
            </label>
            <input
              type="number"
              id="graduationYear"
              value={formData.graduationYear}
              onChange={(e) => handleInputChange('graduationYear', e.target.value)}
              min="1950"
              max={new Date().getFullYear() + 10}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.graduationYear ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder={t('wizard.education.fields.graduationYear.placeholder', 'e.g., 2020')}
            />
            {errors.graduationYear && (
              <p className="mt-1 text-sm text-red-600">{errors.graduationYear}</p>
            )}
          </div>

          {/* Grades */}
          <div className="md:col-span-2">
            <label htmlFor="grades" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.education.fields.grades.label', 'Grades / GPA / Results (Optional)')}
            </label>
            <input
              type="text"
              id="grades"
              value={formData.grades}
              onChange={(e) => handleInputChange('grades', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder={t('wizard.education.fields.grades.placeholder', 'e.g., 3.8 GPA, First Class Honors, A grades')}
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('wizard.education.fields.grades.helper', 'This helps us assess your eligibility for merit-based programs')}
            </p>
          </div>

          {/* Additional Certificates */}
          <div className="md:col-span-2">
            <label htmlFor="additionalCertificates" className="block text-sm font-medium text-gray-700 mb-2">
              {t('wizard.education.fields.additionalCertificates.label', 'Additional Certifications (Optional)')}
            </label>
            <textarea
              id="additionalCertificates"
              value={formData.additionalCertificates}
              onChange={(e) => handleInputChange('additionalCertificates', e.target.value)}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              placeholder={t('wizard.education.fields.additionalCertificates.placeholder', 'List any relevant professional certifications or additional qualifications')}
            />
            <p className="mt-1 text-sm text-gray-500">
              {t('wizard.education.fields.additionalCertificates.helper', 'Include professional licenses, certifications, or specialized training')}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <WizardNavigation
        onPrevious={onPrevious}
        onNext={handleNext}
        previousLabel={t('wizard.common.previous', 'Previous')}
        nextLabel={t('wizard.common.next', 'Next Step')}
        canGoPrevious={canGoPrevious}
        canGoNext={canProceed}
        className="mt-8"
      />
    </div>
  );
};

export default Education;
